import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Player, Room } from '@party-game/shared';

// Define question type
type QuestionType = {
  content: string;
  options: string[];
  timeLimit: number;
} | null;

// Define message type
type MessageType = {
  id: string;
  playerId: string;
  playerName: string;
  message: string;
  timestamp: string;
};

// Game settings type
interface GameSettings {
  spyCount: number;
  category: string;
}

interface GameStore {
  // Player state
  player: Player | null;
  setPlayer: (player: Player | null) => void;

  // Room state
  room: Room | null;
  setRoom: (room: Room | null) => void;

  // Game settings
  gameSettings: GameSettings | null;
  setGameSettings: (settings: GameSettings | null) => void;

  // All players in room
  players: Player[];
  setPlayers: (players: Player[]) => void;
  updatePlayer: (player: Player) => void;
  removePlayer: (playerId: string) => void;

  // Game state
  isHost: boolean;
  setIsHost: (isHost: boolean) => void;
  
  currentRound: number;
  setCurrentRound: (round: number) => void;

  // Spy game specific
  isSpy: boolean;
  setIsSpy: (isSpy: boolean) => void;
  
  secretWord: string | null;
  setSecretWord: (word: string | null) => void;

  // Quiz game specific
  currentQuestion: QuestionType;
  setCurrentQuestion: (question: QuestionType) => void;

  // Chat messages
  messages: MessageType[];
  addMessage: (message: MessageType) => void;

  // Reset
  reset: () => void;
}

const initialState = {
  player: null,
  room: null,
  gameSettings: null,
  players: [],
  isHost: false,
  currentRound: 0,
  isSpy: false,
  secretWord: null,
  currentQuestion: null,
  messages: [],
};

export const useGameStore = create<GameStore>()(
  persist(
    (set) => ({
      ...initialState,

      setPlayer: (player) => set({ player }),
      
      setRoom: (room) => set({ room }),

      setGameSettings: (gameSettings) => set({ gameSettings }),
      
      setPlayers: (players) => set({ players }),
      
      updatePlayer: (player) => set((state) => {
        const exists = state.players.some((p) => p.id === player.id);
        if (exists) {
          return {
            players: state.players.map((p) => 
              p.id === player.id ? player : p
            ),
          };
        } else {
          return {
            players: [...state.players, player],
          };
        }
      }),
      
      removePlayer: (playerId) => set((state) => ({
        players: state.players.filter((p) => p.id !== playerId),
      })),

      setIsHost: (isHost) => set({ isHost }),
      
      setCurrentRound: (currentRound) => set({ currentRound }),

      setIsSpy: (isSpy) => set({ isSpy }),
      
      setSecretWord: (secretWord) => set({ secretWord }),

      setCurrentQuestion: (currentQuestion) => set({ currentQuestion }),

      addMessage: (message) => set((state) => ({
        messages: [...state.messages.slice(-50), message], // Keep last 50 messages
      })),

      reset: () => set(initialState),
    }),
    {
      name: 'party-game-storage',
      partialize: (state) => ({
        player: state.player,
        room: state.room,
        gameSettings: state.gameSettings,
      }),
    }
  )
);

// Selectors
export const selectSortedPlayers = (state: GameStore) => 
  [...state.players].sort((a, b) => b.score - a.score);

export const selectHostPlayer = (state: GameStore) =>
  state.players.find((p) => p.role === 'host');

export const selectPlayerById = (playerId: string) => (state: GameStore) =>
  state.players.find((p) => p.id === playerId);
